import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { OrgActions } from "@/components/OrgActions";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Building2, Plus, Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Organisations() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState({ name: "", legal_name: "", address: "", state_code: "" });

  const { data: orgs = [], isLoading } = useQuery({
    queryKey: ["orgs"],
    queryFn: async () => (await api.get("/admin/organisations")).data,
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/admin/organisations", form)).data,
    onSuccess: () => {
      toast.success("Organisation created");
      setOpen(false);
      setForm({ name: "", legal_name: "", address: "", state_code: "" });
      qc.invalidateQueries({ queryKey: ["orgs"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const archivedCount = orgs.filter((o) => o.archived).length;
  const visible = orgs.filter((o) => showArchived || !o.archived);

  return (
    <div>
      <PageHeader
        title="Organisations"
        description="Create and manage client organisations"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-org-btn"><Plus className="mr-2 h-4 w-4" /> New organisation</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create organisation</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Name</Label><Input value={form.name} onChange={upd("name")} className="mt-1.5" data-testid="org-name" /></div>
                <div><Label>Legal name</Label><Input value={form.legal_name} onChange={upd("legal_name")} className="mt-1.5" data-testid="org-legal-name" /></div>
                <div><Label>Address</Label><Input value={form.address} onChange={upd("address")} className="mt-1.5" data-testid="org-address" /></div>
                <div><Label>State code (e.g. WB)</Label><Input value={form.state_code} maxLength={2} onChange={upd("state_code")} className="mt-1.5 uppercase" data-testid="org-state-code" /></div>
              </div>
              <DialogFooter>
                <Button disabled={!form.name || create.isPending} onClick={() => create.mutate()} data-testid="org-create-submit">
                  {create.isPending ? "Creating…" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <label className="mb-4 flex w-fit items-center gap-2 text-sm text-slate-600">
        <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="show-archived-toggle" />
        Show archived ({archivedCount})
      </label>

      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : visible.length === 0 ? (
        <EmptyState title="No organisations yet" hint="Create your first client organisation" icon={Building2} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((o) => (
            <Card key={o.id} data-testid={`org-card-${o.id}`}
              className={`cursor-pointer p-5 transition-shadow hover:shadow-md ${o.archived ? "bg-slate-50 opacity-70" : ""}`}
              onClick={() => navigate(`/admin/organisations/${o.id}`)}>
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="flex items-center gap-2">
                  {o.archived && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700" data-testid={`org-archived-badge-${o.id}`}>Archived</span>}
                  {o.state_code && <span className="text-xs font-medium text-slate-400">{o.state_code}</span>}
                  <OrgActions org={o} />
                </div>
              </div>
              <p className="mt-3 font-semibold text-slate-900">{o.name}</p>
              {o.legal_name && <p className="text-xs text-slate-400">{o.legal_name}</p>}
              <div className="mt-4 flex gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {o.employee_count} employees</span>
                <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> {o.admin_count} admins</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
